import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { Price } from '@/client/types/entity-full';
import {
  UpsertPricePayload,
  UpdatePricePayload,
  PriceMutationResponse,
} from '@/client/types/promotion-pricing';
import { promotionPricingKeys } from './promotion-pricing.query';
import { tradeableCatalogKeys } from './tradeable-catalog.query';

/** Refetch every list that could show the mutated price. */
const invalidatePriceLists = (
  queryClient: ReturnType<typeof useQueryClient>,
): void => {
  queryClient.invalidateQueries({ queryKey: promotionPricingKeys.lists() });
  queryClient.invalidateQueries({ queryKey: tradeableCatalogKeys.lists() });
};

// ─── Upsert Price ───────────────────────────────────────────────────────────────
// Idempotent on (prictid + ccode + country_iso2 + toid): creates on first call,
// updates thereafter. Backend returns 201 (created) or 200 (updated).

const useUpsertPriceFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async (payload: UpsertPricePayload): Promise<Price> => {
      const response = await request<PriceMutationResponse>('/api/admin/prices', 'POST', payload);
      return response.data;
    },
    'Failed to save price',
  );
};

export const useUpsertPriceMutation = () => {
  const queryClient = useQueryClient();
  const upsertFn = useUpsertPriceFn();
  return useMutation({
    mutationFn: (payload: UpsertPricePayload) => upsertFn(payload),
    onSuccess: () => {
      invalidatePriceLists(queryClient);
    },
  });
};

// ─── Update Price ───────────────────────────────────────────────────────────────

const useUpdatePriceFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async (pricid: number, payload: UpdatePricePayload): Promise<Price> => {
      const response = await request<PriceMutationResponse>(
        `/api/admin/prices/${pricid}`,
        'PUT',
        payload,
      );
      return response.data;
    },
    'Failed to update price',
  );
};

export const useUpdatePriceMutation = () => {
  const queryClient = useQueryClient();
  const updateFn = useUpdatePriceFn();
  return useMutation({
    mutationFn: ({ pricid, payload }: { pricid: number; payload: UpdatePricePayload }) =>
      updateFn(pricid, payload),
    onSuccess: () => {
      invalidatePriceLists(queryClient);
    },
  });
};

// ─── Delete Price ───────────────────────────────────────────────────────────────

const useDeletePriceFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async (pricid: number): Promise<{ status: string }> => {
      const response = await request<{ status: number; data: { status: string } }>(
        `/api/admin/prices/${pricid}`,
        'DELETE',
      );
      return response.data;
    },
    'Failed to delete price',
  );
};

export const useDeletePriceMutation = () => {
  const queryClient = useQueryClient();
  const deleteFn = useDeletePriceFn();
  return useMutation({
    mutationFn: ({ pricid }: { pricid: number }) => deleteFn(pricid),
    onSuccess: () => {
      invalidatePriceLists(queryClient);
    },
  });
};
